import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminBlogPosts = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_published: '',
    is_featured: '',
    category: '',
    search: '',
    ...initialFilters
  });

  const cleanFilters = useMemo(() => {
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
    if (!cleaned.per_page) {
      cleaned.per_page = 10;
    }
    return cleaned;
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: postsResponse, isLoading, error } = useQuery({
    queryKey: ['adminBlogPosts', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/blog-posts${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const { data: statisticsResponse } = useQuery({
    queryKey: ['adminBlogPostStatistics'],
    queryFn: async () => {
      const response = await api.get('/admin/blog-posts/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/blog-posts', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBlogPosts']);
      queryClient.invalidateQueries(['adminBlogPostStatistics']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/blog-posts/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBlogPosts']);
      queryClient.invalidateQueries(['adminBlogPostStatistics']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/blog-posts/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBlogPosts']);
      queryClient.invalidateQueries(['adminBlogPostStatistics']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/blog-posts/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBlogPosts']);
      queryClient.invalidateQueries(['adminBlogPostStatistics']);
    }
  });

  const posts = useMemo(() => {
    if (postsResponse?.data?.data) {
      return postsResponse.data.data;
    }
    return postsResponse?.data || [];
  }, [postsResponse]);

  const statistics = useMemo(() => {
    return statisticsResponse?.data || {
      total_posts: 0,
      published_posts: 0,
      draft_posts: 0,
      featured_posts: 0,
      total_views: 0
    };
  }, [statisticsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return {
    posts,
    isLoading,
    error,
    filters,
    handleFilterChange,
    statistics,
    createPost: (data, options) => createMutation.mutate(data, options),
    isCreating: createMutation.isPending,
    updatePost: (data, options) => updateMutation.mutate(data, options),
    isUpdating: updateMutation.isPending,
    toggleStatus: (id, options) => toggleStatusMutation.mutate(id, options),
    isToggling: toggleStatusMutation.isPending,
    deletePost: (id, options) => deleteMutation.mutate(id, options),
    isDeleting: deleteMutation.isPending
  };
};

